
import React, { useState } from 'react';
import { dbService } from '../dbService';
import { ContactSettings } from '../types';

interface ContactSectionProps {
  settings: ContactSettings;
}

const ContactSection: React.FC<ContactSectionProps> = ({ settings }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    projectType: 'Highway Expansion',
    message: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      // Save to Firestore
      await dbService.saveInquiry(formData);
      
      // Send Email via Backend API
      try {
        const response = await fetch('/api/contact', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          const detailedError = errorData.details?.message || errorData.error || 'Unknown email error';
          console.warn('Email notification failed:', detailedError);
          // We don't block the UI success if it's just the email that failed, 
          // but we can log it or show a subtle warning.
          // For debugging, let's show it if it's a specific Resend error.
          if (errorData.details) {
            setError(`Inquiry saved, but email failed: ${detailedError}`);
            return; // Stop here so user sees the error
          }
        }
      } catch (emailError) {
        console.error('Error sending email notification:', emailError);
      }

      setSubmitted(true);
      setFormData({ name: '', email: '', projectType: 'Highway Expansion', message: '' });
      setError(null);
    } catch (error: any) {
      setError(`Submission failed: ${error.message || "Please try again or contact us directly."}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 px-4 transition-colors bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-start">
        <div>
          <h2 className="text-blue-800 dark:text-blue-400 font-black text-sm uppercase tracking-widest mb-2">Connect</h2>
          <h3 className="text-4xl font-extrabold text-gray-900 dark:text-white mb-6">Let's Discuss Your Next Infrastructure Project</h3>
          <p className="text-gray-600 dark:text-gray-400 text-lg mb-10">
            From initial survey to final paving, Sai SSK Infrastructures is your partner in building durable, high-capacity roads. Reach out for consultations or partnership inquiries.
          </p>
          
          <div className="space-y-6">
            <div className="flex gap-4 group">
              <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center text-blue-800 dark:text-blue-400 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
              </div>
              <div>
                <div className="font-bold text-gray-900 dark:text-white">Phone Support</div>
                <div className="text-gray-600 dark:text-gray-400">{settings.phone}</div>
              </div>
            </div>
            
            <div className="flex gap-4 group">
              <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center text-blue-800 dark:text-blue-400 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              </div>
              <div>
                <div className="font-bold text-gray-900 dark:text-white">Email Inquiry</div>
                <div className="text-gray-600 dark:text-gray-400">{settings.email}</div>
              </div>
            </div>

            <div className="flex gap-4 group">
              <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center text-blue-800 dark:text-blue-400 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              </div>
              <div>
                <div className="font-bold text-gray-900 dark:text-white">Headquarters</div>
                <div className="text-gray-600 dark:text-gray-400 whitespace-pre-line">{settings.address}</div>
              </div>
            </div>

            {settings.mapUrl && (
              <div className="mt-8 rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-700 shadow-lg h-64">
                <iframe 
                  src={settings.mapUrl}
                  width="100%" 
                  height="100%" 
                  style={{ border: 0 }} 
                  allowFullScreen={true} 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Sai SSK Infrastructures Office Location"
                ></iframe>
              </div>
            )}
          </div>
        </div>

        <div className="relative">
          {submitted ? (
            <div className="bg-white dark:bg-gray-800 p-12 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-xl text-center animate-in zoom-in duration-500">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Message Received!</h4>
              <p className="text-gray-600 dark:text-gray-400 mb-8">One of our specialist engineers will reach out to you within 24 hours.</p>
              <button 
                onClick={() => setSubmitted(false)}
                className="text-blue-800 dark:text-blue-400 font-bold hover:underline"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 p-8 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-xl space-y-6 transition-colors">
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-xl text-sm font-bold border border-red-100 dark:border-red-900/40">
                  {error}
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Full Name</label>
                  <input 
                    required
                    type="text" 
                    className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 dark:text-white outline-none transition" 
                    placeholder="John Doe" 
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Email Address</label>
                  <input 
                    required
                    type="email" 
                    className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 dark:text-white outline-none transition" 
                    placeholder="john@example.com" 
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Project Type</label>
                <select 
                  className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 dark:text-white outline-none transition"
                  value={formData.projectType}
                  onChange={(e) => setFormData({...formData, projectType: e.target.value})}
                >
                  <option>Highway Expansion</option>
                  <option>Urban Development</option>
                  <option>Bridge & Structure</option>
                  <option>Maintenance & Repair</option>
                  <option>Other</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Message</label>
                <textarea 
                  required
                  rows={4} 
                  className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 dark:text-white outline-none transition" 
                  placeholder="Tell us about your project requirements..."
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                ></textarea>
              </div>

              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-blue-800 dark:bg-blue-600 text-white py-4 rounded-lg font-bold hover:bg-blue-700 dark:hover:bg-blue-500 transition shadow-lg shadow-blue-200 dark:shadow-none disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Submit Inquiry'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
